import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { ShieldCheck } from "lucide-react";
import { logDev } from "@/lib/logger";

/**
 * Reusable hCaptcha field wrapper.
 * - Uses REACT_APP_HCAPTCHA_SITE_KEY (falls back to hCaptcha's official test key that always passes).
 * - Renders nothing (and provides a no-op ref) if the parent passes `skip` (e.g., authenticated user).
 * - Parent gets the token via onVerify(token) and can call ref.current.reset() after submit.
 * - Shows a "still loading" fallback if the widget takes >5s (typical of blocked 3rd-party iframes).
 */
const HCaptchaField = forwardRef(function HCaptchaField({ onVerify, onExpire, skip }, ref) {
  const captchaRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [slow, setSlow] = useState(false);
  const siteKey = process.env.REACT_APP_HCAPTCHA_SITE_KEY || "10000000-ffff-ffff-ffff-000000000001";

  useImperativeHandle(ref, () => ({
    reset: () => {
      try { captchaRef.current?.resetCaptcha?.(); } catch (err) { logDev.warn("hCaptcha reset failed:", err); }
      setLoaded(false);
    },
  }), []);

  useEffect(() => {
    if (skip || loaded) return;
    const t = setTimeout(() => setSlow(true), 5000);
    return () => clearTimeout(t);
  }, [skip, loaded]);

  if (skip) return null;

  return (
    <div data-testid="hcaptcha-field" className="border border-gold/40 bg-gold/5 p-3 flex flex-col items-center gap-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-ink">
        <ShieldCheck className="w-3.5 h-3.5 text-gold" />
        <span>Please verify you're human</span>
      </div>
      <HCaptcha
        ref={captchaRef}
        sitekey={siteKey}
        theme="light"
        size="normal"
        onLoad={() => setLoaded(true)}
        onVerify={(token) => onVerify?.(token)}
        onExpire={() => { onExpire?.(); }}
        onError={() => { onExpire?.(); }}
      />
      {slow && !loaded && (
        <p className="text-[11px] text-muted-foreground text-center max-w-xs">
          Captcha is taking long to load. If nothing appears, your browser may be blocking third-party frames. Try Chrome or Safari, or turn off any privacy extensions.
        </p>
      )}
    </div>
  );
});

export default HCaptchaField;
